/**
 * FeaturedCurators
 *
 * Shown on the main dashboard (and optionally during onboarding) to introduce
 * the three founding curators.  Follows the LinkedIn "People you may know"
 * pattern: invitation-only badge, short tagline, follow button, link to the
 * full public profile.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface Curator {
  id: string;
  username: string;
  full_name: string | null;
  avatar_url: string | null;
  curator_tagline: string | null;
  follower_count: number;
  is_following: boolean;
}

const AvatarFallback: React.FC<{ name: string | null }> = ({ name }) => {
  const initials = (name ?? '?')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return (
    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-sm font-semibold shrink-0">
      {initials}
    </div>
  );
};

interface FeaturedCuratorsProps {
  /** When true, shows "Follow all" CTA — ideal for onboarding step. */
  onboardingMode?: boolean;
  onAllFollowed?: () => void;
}

export const FeaturedCurators: React.FC<FeaturedCuratorsProps> = ({
  onboardingMode = false,
  onAllFollowed,
}) => {
  const { user } = useAuth();
  const [curators, setCurators] = useState<Curator[]>([]);
  const [loading, setLoading] = useState(true);
  const [followingId, setFollowingId] = useState<string | null>(null);

  const fetchCurators = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.rpc('get_featured_curators', {
      p_user_id: user?.id ?? null,
    });
    if (!error && data) setCurators(data as Curator[]);
    setLoading(false);
  }, [user?.id]);

  useEffect(() => { fetchCurators(); }, [fetchCurators]);

  const toggleFollow = async (curator: Curator) => {
    if (!user) {
      toast.error('Sign in to follow curators.');
      return;
    }
    setFollowingId(curator.id);
    try {
      if (curator.is_following) {
        await supabase
          .from('curator_follows')
          .delete()
          .eq('follower_id', user.id)
          .eq('curator_id', curator.id);
        setCurators((prev) =>
          prev.map((c) =>
            c.id === curator.id
              ? { ...c, is_following: false, follower_count: Math.max(c.follower_count - 1, 0) }
              : c,
          ),
        );
      } else {
        await supabase
          .from('curator_follows')
          .insert({ follower_id: user.id, curator_id: curator.id });
        setCurators((prev) =>
          prev.map((c) =>
            c.id === curator.id
              ? { ...c, is_following: true, follower_count: c.follower_count + 1 }
              : c,
          ),
        );
      }
    } catch {
      toast.error('Something went wrong. Try again.');
    } finally {
      setFollowingId(null);
    }
  };

  const followAll = async () => {
    if (!user) { toast.error('Sign in to follow curators.'); return; }
    const unfollowed = curators.filter((c) => !c.is_following && c.id !== user.id);
    for (const c of unfollowed) {
      await supabase
        .from('curator_follows')
        .insert({ follower_id: user.id, curator_id: c.id })
        .then(() => {
          setCurators((prev) =>
            prev.map((x) =>
              x.id === c.id ? { ...x, is_following: true, follower_count: x.follower_count + 1 } : x,
            ),
          );
        });
    }
    toast.success('Following all founding curators!');
    onAllFollowed?.();
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-border bg-card p-6 animate-pulse">
        <div className="h-4 bg-muted rounded w-32 mb-4" />
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-muted" />
            <div className="flex-1 space-y-1.5">
              <div className="h-3 bg-muted rounded w-28" />
              <div className="h-3 bg-muted rounded w-48" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (curators.length === 0) return null;

  const allFollowed = curators.filter((c) => c.id !== user?.id).every((c) => c.is_following);

  return (
    <div className="rounded-xl border border-border bg-card p-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1">
            Founding Curators
          </div>
          <h3 className="text-base font-semibold tracking-tight">
            {onboardingMode ? 'Start with the best feeds' : 'People worth following'}
          </h3>
          {onboardingMode && (
            <p className="mt-1 text-xs text-muted-foreground leading-relaxed max-w-xs">
              These are invitation-only curators selected by SocialScoop. Follow them to seed your
              Following feed from day one.
            </p>
          )}
        </div>
        {!allFollowed && (
          <Button size="sm" variant="outline" onClick={followAll} className="shrink-0 text-xs">
            Follow all
          </Button>
        )}
      </div>

      {/* Curator list */}
      <div className="space-y-4">
        {curators.map((curator) => (
          <div key={curator.id} className="flex items-center gap-3">
            {curator.avatar_url ? (
              <img
                src={curator.avatar_url}
                alt={curator.full_name ?? curator.username}
                className="w-10 h-10 rounded-full object-cover shrink-0"
              />
            ) : (
              <AvatarFallback name={curator.full_name} />
            )}

            <div className="flex-1 min-w-0">
              <Link
                to={`/u/${curator.username}`}
                className="text-sm font-medium hover:underline underline-offset-4 truncate block"
              >
                {curator.full_name ?? `@${curator.username}`}
              </Link>
              {curator.curator_tagline && (
                <p className="text-xs text-muted-foreground truncate">{curator.curator_tagline}</p>
              )}
              <p className="text-xs text-muted-foreground tabular-nums">
                {curator.follower_count.toLocaleString()}{' '}
                {curator.follower_count === 1 ? 'follower' : 'followers'}
              </p>
            </div>

            {user?.id !== curator.id && (
              <Button
                size="sm"
                variant={curator.is_following ? 'outline' : 'default'}
                className="shrink-0 text-xs h-8 px-3"
                onClick={() => toggleFollow(curator)}
                disabled={followingId === curator.id}
              >
                {followingId === curator.id
                  ? '…'
                  : curator.is_following
                  ? 'Following'
                  : 'Follow'}
              </Button>
            )}
          </div>
        ))}
      </div>

      {!onboardingMode && (
        <div className="mt-5 flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            Curator status is invitation-only.
          </p>
          <Link
            to="/dashboard/explore"
            className="text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline shrink-0 ml-2"
          >
            Browse all →
          </Link>
        </div>
      )}
    </div>
  );
};

export default FeaturedCurators;
